import { LitElement, html, css, HTMLTemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import '../../../vessel/src/main-app'
import "@maersk-global/mds-components-core";
import { MdsConfig } from "@maersk-global/mds-config";
import { MigrationChangeEvents, MigrationState, setVesselMigrationModel, VesselMigrationModel } from '../../../src/models/vessel-migration-model';
import { VesselMigrationModelImpl } from '../../../src/models/vessel-migration-model-impl';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { AuthService } from '../../../src/services/auth/auth-service';
import { IMO, setVoyageImoModel, VoyageImoModel } from '../../../src/models/voyage-imo-model';
import { VoyageImoEventNames, VoyageImoModelImpl } from '../../../src/models/voyage-imo-model-impl';
import '../../imo-input-popup/src/imo-input-popup';
import '../../voy-loader/src/voy-loader';
import { IndexedDBSyncService } from '../../../indexed-db/services/sync_db_service';

const isAuthRequired = (<any>window)["appenv"].isAuthRequired;
const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
const isK8s = (<any>window)["appenv"].isK8s;
const vesselImoNumber = (<any>window)["appenv"].vesselIMONumber;
MdsConfig.iconsDynamicImportPath = "./assets/styles/design_system/";

@customElement('vessel-app-switcher')
export class VesselAppSwitcher extends LitElement
{
  constructor() {
	  super();
	  
	  setVesselMigrationModel(new VesselMigrationModelImpl());

	  this._appSwitcher = this._appSwitcher.bind(this);
	  this._onMigrationStateChanged = this._onMigrationStateChanged.bind(this);
  }

	@property({ attribute: false }) private _migrationState: MigrationState | null = null;
	@property({ attribute: false }) private _showMigrationApp: boolean = false;
	@property({ attribute: false }) private _isAuthenticated: boolean = false;
	private _vesselImoNumber: IMO = null;
    
    static get styles()
    {
        return [
            css`
                .migration-app {
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  height: 100vh;
                  width: 100vw;
                  background: #00243D;
				  z-index: 9999;
				  position: absolute;
                  
                }
				.migration-panel {
					position: absolute;
					top: 50%;
					left: 50%;
					transform: translate(-50%, -50%);
					width: 423px;
					height: 240px;
				}
				.migration-message {
					display: flex;
					flex-direction: column;
					margin: 10px auto 0 auto;
					font-size: 14px;
				}
				.k8s-link {
					margin-top: 11px;
				}
				.migration-container {
					height: 100%;
					display: flex;
					flex-direction: column;
					justify-content: center;
				}

				.completed-message {
					display: flex;
                    box-sizing: border-box;
                    align-items: center;
                    width: 100%;
                    height: 44px;
                    background-color: var(--feedback-sucess-light, lightgreen);
                    border-radius: 4px;
                    padding: 12px;
                    margin-bottom: 6px;
                    pointer-events: none;
				}
				.completed-message svg {
                    width: 16px;
                    margin-right: 14px;
                }

				.completed-text {
					padding-bottom: 4px;
				}

				.container {
                    width: 100%;
					height: 100%;
                    position: relative;
					background-color: white;
                }
            `
        ];
	}
	
	connectedCallback() {
		super.connectedCallback();

		setVoyageImoModel(new VoyageImoModelImpl());
		this._vesselImoNumber = VoyageImoModel!.imo;

		VesselMigrationModel!.addEventListener(MigrationChangeEvents.StateChanged, this._onMigrationStateChanged);
		VoyageImoModel?.addEventListener(VoyageImoEventNames.ImoUpdated, () => this._updateImo());

		AuthService.addEventListener('login-required', () => {
			console.log('Login required event detected, redirecting to login...');
			AuthService.doAuth();
		});
	
		if (isVesselOnCloud) {
            AuthService._handleRedirectResponse().then(() => {
                this._checkAuthentication();
                if (this._isAuthenticated && isVesselOnCloud) {
                    VoyageImoModel?.intialiseImo();
                    if (VoyageImoModel!.imo)
                        this._vesselImoNumber = VoyageImoModel!.imo;
                }
            }).catch(error => {
                console.error("Error during redirect response handling:", error);
            });
        }
	}
	
    private _updateImo = async () => {
        this._vesselImoNumber = VoyageImoModel!.imo;
		// clear all background sync when ever imo changes
           await IndexedDBSyncService.request.deleteAll();
		this.requestUpdate();        
    }
	
	disconnectedCallback()
    {
        super.disconnectedCallback();
		VesselMigrationModel!.removeEventListener(MigrationChangeEvents.StateChanged, this._onMigrationStateChanged);
		VoyageImoModel?.removeEventListener('imo-updated', () => this._updateImo());
		const loginRequiredHandler = () => AuthService.doAuth();
    	AuthService.removeEventListener('login-required', loginRequiredHandler);

    }

	private async _checkAuthentication() {
        if (!isAuthRequired && !isVesselOnCloud) {
            this._isAuthenticated = true;
            return;
        }

        if (AuthService.isLoggedIn) {
            this._isAuthenticated = true;
        } else {
            AuthService.addEventListener('logged-in', () => {
                this._isAuthenticated = true;
                this.requestUpdate();
            });

            AuthService.addEventListener('login-required', () => {
                AuthService.doAuth();
            });

            AuthService.doAuth();
        }
    }
  
	render() {

		if (isVesselOnCloud) {
			if (!this._isAuthenticated) {
				return html`<voy-loader label="Authenticating..." .showLoader=${true}></voy-loader>`;
			}

			if (!this._vesselImoNumber && AuthService.isLoggedIn && (sessionStorage.getItem('isVesselUser') === 'No')) {
				return this._imoPopupElement();
			}
		}

      	return html`${this._appSwitcher()}`
	}

	private _onMigrationStateChanged() {
		const newState = VesselMigrationModel!.migrationState;
		this._migrationState = newState;

		if (this._migrationState === MigrationState.MigrationFailed
		|| this._migrationState === MigrationState.MigrationServiceNotFound) {
			this._showMigrationApp = false;
		}

		// if not k8s, then show migration screen
		if (!isK8s && this._migrationState === MigrationState.MigrationCompleted) {
			this._showMigrationApp = true;
			return;
		}

		if (this._migrationState === MigrationState.MigrationToNextPlatformInProgress) {
			this._showMigrationApp = true;
		}
	}
	
	private _appSwitcher() {
		return html`
			<main-app></main-app>
			`
    }
    
	private _migarationApp(): HTMLTemplateResult {
		return html`
			<main class="migration-app">
				<voy-panel class="migration-panel" ?shown=${true} panelTitle="Migrate">
					${this._getMigrationElement()}
				</voy-panel>
			</main>
		`	
	}

	private _getMigrationElement() {
		if (this._migrationState === MigrationState.MigrationToNextPlatformInProgress) {
			return this._isMigratingElement();
		}

		if (this._migrationState === MigrationState.MigrationCompleted) {
			return this._migrationCompletedElement();
		}
	}

	private _isMigratingElement() {
		return html`
			<div class="migration-container">
				<mc-loading-indicator label=""></mc-loading-indicator>
				<div class="migration-message">
					<span>Migrating to next platform, thank you for your patience</span>
				</div>
			</div>
		`
	}

	private _migrationCompletedElement() {
		return html`
			<div class="migration-container completed">
				<div class="completed-message">
					<span>${SvgIcons.lightCheckCircle}</span>
					<span class="completed-text">Migration has been generated successfully</span>
				</div>
				<div class="migration-message">
					<span>Please copy the following link into your browser, and use it from no on.</span>
					<span class="k8s-link">https://${vesselImoNumber}cvk3s01/voyage/</span>
				</div>
			</div>
		`
	}

	private _imoPopupElement() {
		return html`
		 <div class="container">
			<imo-input-popup
				.isOpen=${true}
			></imo-input-popup>
		<div class="main">
		`;
	}
}
